"use client";

import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  useCallback,
  useRef,
} from "react";

const DETAIL_OPEN_POINTER_MOVE_THRESHOLD = 12;

type UseSituationHeaderItemGesturesParams = {
  canOpenDetail: boolean;
  onOpenDetail: () => void;
};

export default function useSituationHeaderItemGestures({
  canOpenDetail,
  onOpenDetail,
}: UseSituationHeaderItemGesturesParams) {
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const didPointerMoveRef = useRef(false);

  const handlePointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    pointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
    };
    didPointerMoveRef.current = false;
  }, []);

  const handlePointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
    const pointerStart = pointerStartRef.current;

    if (!pointerStart) {
      return;
    }

    const deltaX = Math.abs(event.clientX - pointerStart.x);
    const deltaY = Math.abs(event.clientY - pointerStart.y);

    if (
      deltaX > DETAIL_OPEN_POINTER_MOVE_THRESHOLD ||
      deltaY > DETAIL_OPEN_POINTER_MOVE_THRESHOLD
    ) {
      didPointerMoveRef.current = true;
    }
  }, []);

  const handleCardClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!canOpenDetail) {
        return;
      }

      if (didPointerMoveRef.current) {
        event.preventDefault();
        didPointerMoveRef.current = false;
        pointerStartRef.current = null;
        return;
      }

      onOpenDetail();
    },
    [canOpenDetail, onOpenDetail],
  );

  const handleCardKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!canOpenDetail) {
        return;
      }

      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      onOpenDetail();
    },
    [canOpenDetail, onOpenDetail],
  );

  return {
    handleCardClick,
    handleCardKeyDown,
    handlePointerDown,
    handlePointerMove,
  };
}
